import { useEffect, useState } from "react";
import { GeneralCommonHeader } from "../common/GeneralCommonHeader";
import { UnansweredInfo } from "../../features/components/top/ui_elements/UnansweredInfo";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DISPLAY_HEADER_ICON_NONE,
  DISPLAY_PAGE_TOP,
  LANGUAGE_JAPANESE,
  LANGUAGE_ENGLISH,
  PAGE_NAME_FAVORITE,
  PAGE_NAME_SEARCH_HISTORY,
  PAGE_NAME_SELECT_MODEL,
  SPLASH_DISPLAY_TIME,
} from "../../constants/common_constants";
import { calcUtcDatetime } from "../../utils/basis_util";
import { useDtsAppContext } from "../provider/DtsAppContextProvider";
import { getUnanswered } from "../../features/api/dts_api_wrapper";
import { useApi, useErrorDialog, useInitUsageLog } from "../../hooks";
import { useTranslation } from "react-i18next";
import { HamburgerMenu } from "../../features/components/top/ui_elements/HamburgerMenu";
import start from "../../icons/Start.svg";
import History from "../../icons/History.svg";
import Favorite from "../../icons/Favorite.svg";

// トップ画面
export const Top = () => {
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();
  // 他のページから遷移された時の処理
  const location = useLocation();
  //ログイン画面から遷移してきたかどうかを格納
  const fromLoginPage: boolean = location.state?.isFromLogin ?? false;

  const [unansweredNum, setUnansweredNum] = useState(0); //未回答数
  //ハンバーガーメニューの展開状態を格納
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const {
    splash,
    setDacUserId,
    setDacSubjectName,
    setDacModel,
    setDacMachineNumber,
    setDacInferenceFactor,
    setDacSearchHistoryId,
    setDacLanguage,
    setDacSearchHistorySortOrder,
    setDacSearchHistoryPageNum,
    setDacFilterItem,
    setDacFilterCriteria,
    setDacFavoriteSortOrder,
    setDacFavoritePageNum,
    setSplash,
    setDacDtcSearchData,
    setDacModelType,
    setDacOccurrenceTiming,
  } = useDtsAppContext();
  //LocalStorageから認証ユーザー情報を取得
  const userId =
    localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`
    ) || "";
  const displayPage = DISPLAY_PAGE_TOP;
  const displayDateTime = calcUtcDatetime(); //現在日時(UTC時刻)
  //LocalStorageから言語情報を取得、なければブラウザ言語を取得
  const language =
    localStorage.getItem("language") || navigator.language.split("_")[0];

  const { handleApi } = useApi();

  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  // 利用ログ登録
  const { handleInit: initUsageLog } = useInitUsageLog({
    userId,
    displayPage,
    displayDateTime,
  });
  useEffect(() => {
    handleTopPageInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //ページ表示時に行うTopPageの処理
  const handleTopPageInit = async () => {
    try {
      if (
        fromLoginPage &&
        (sessionStorage.getItem("splash") === null || splash)
      ) {
        //ログイン画面から遷移した かつ (スプラッシュ画面の表示フラグがセッションストレージから取れない(初回) または スプラッシュ画面の表示フラグがtrue)の場合,
        //未回答数の取得とスプラッシュ画面の処理を実行
        const promise = [handleGetUnanswerd(), displaySplashScreen()];
        const responses = await Promise.all(promise);
        if (responses.every((result) => result)) {
          //Promise.allの結果が全部true(正常)だった場合, スプラッシュ画面の表示フラグをfalseにする
          setSplash(false);
          sessionStorage.setItem("splash", String(false));
        }
      } else {
        // その他の場合,未回答数の取得のみ(スプラッシュ画面の処理をしない)
        await handleGetUnanswerd();
      }
      // Context、LocalStorage(認証情報以外)のクリア
      InitializeContextAndLocalStorage();

      if (userId) {
        //コンテキストにユーザーIDを保存する
        setDacUserId(userId);
        //Memo: userIdはCognitoの認証情報を使う為、改めてローカルストレージに保存することはしない
      }

      if (language && language === LANGUAGE_JAPANESE) {
        setDacLanguage(language);
        localStorage.setItem("language", language);
      } else {
        setDacLanguage(LANGUAGE_ENGLISH);
        localStorage.setItem("language", LANGUAGE_ENGLISH);
      }

      console.log("handleTopPageInit start.");
      //利用ログ登録
      const isError = await initUsageLog();
      if (isError) {
        return;
      }
    } catch (err) {
      //未回答数取得、内部エラー時にここでキャッチする
      console.log(`[${displayPage}]err: `, err);
      if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        //LocalStorageのセッション情報クリア
        const userpool_client_id =
          process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
        );

        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
          "reload"
        );
        //エラー", "通信エラーが発生しました。
      }
    }
  };

  //未回答数取得の処理(呼び出し元でのエラー処理必須)
  const handleGetUnanswerd = async () => {
    //未回答数取得
    const unanswered = await handleApi(getUnanswered, userId);
    if ("unansweredNum" in unanswered) {
      const num = unanswered.unansweredNum ?? -1;
      if (num !== -1) {
        setUnansweredNum(num);
      }
    } else {
      console.log("failed to unanswered.");
      throw new Error("failed to unanswered.");
    }
    //trueを返す
    return true;
  };

  //スプラッシュ画面の処理
  const displaySplashScreen = async () => {
    sessionStorage.setItem("splash", String(true));
    setSplash(true);
    await waitTimeMs(SPLASH_DISPLAY_TIME);
    //tureを返す
    return true;
  };
  // 非同期関数としてタイマーを実装
  const waitTimeMs = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  //Context、LocalStorage(認証情報以外)のクリア
  const InitializeContextAndLocalStorage = () => {
    //ユーザーID削除(LocalStorageには存在しないのでContextのみ)
    setDacUserId("");
    //案件名削除
    setDacSubjectName("");
    localStorage.removeItem("subjectName");
    //型式削除
    setDacModel("");
    localStorage.removeItem("model");
    //機番削除
    setDacMachineNumber("");
    localStorage.removeItem("machineNumber");
    //推定要因項目削除
    setDacInferenceFactor("");
    localStorage.removeItem("inferenceFactor");
    //検索履歴情報ID削除
    setDacSearchHistoryId("");
    localStorage.removeItem("searchHistoryId");
    //dtsSearchDataの削除
    setDacDtcSearchData("");
    localStorage.removeItem("dtcSearchData");
    //modelTypeの削除
    setDacModelType("");
    localStorage.removeItem("modelType");
    //occurrenceTimingの削除
    setDacOccurrenceTiming(0);
    localStorage.removeItem("occurrenceTiming");

    //マニュアルの参照先削除(LocalStorageのみ)
    //memo:キーはreference_UUIDの形式で複数保存されている可能性がある
    const prefix = "reference";
    const keyToRemove = Object.keys(localStorage).filter((key) =>
      key.startsWith(prefix)
    );
    keyToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
    //タイトル(参照情報のラベル)削除(LocalStorageのみ)
    localStorage.removeItem("title");
    //検索履歴画面の並び順削除
    setDacSearchHistorySortOrder("desc");
    localStorage.removeItem("searchHistorySortOrder");
    //検索履歴画面のページ番号削除
    setDacSearchHistoryPageNum(0);
    localStorage.removeItem("searchHistoryPageNum");
    //検索履歴画面のフィルター項目削除
    setDacFilterItem("");
    localStorage.removeItem("filterItem");
    //検索履歴画面のフィルター条件削除
    setDacFilterCriteria("");
    localStorage.removeItem("filterCriteria");
    //お気に入り画面の並び順削除
    setDacFavoriteSortOrder("desc");
    localStorage.removeItem("favoriteSortOrder");
    //お気に入り画面のページ番号削除
    setDacFavoritePageNum(0);
    localStorage.removeItem("favoritePageNum");
  };

  return (
    <div className="flex flex-col h-svh">
      <GeneralCommonHeader
        title={t("トップ画面.トラブルシューティング")} //トラブルシューティング
        displayheaderIcon={DISPLAY_HEADER_ICON_NONE}
        displayheaderMenuIcon={true}
        onMenuBtnClick={() => setMenuOpen((prevState) => !prevState)}
      />
      <main className="h-full">
        <div className="h-full text-center py-14 flex flex-col">
          <div className="flex flex-col items-center justify-center gap-2">
            <button
              onClick={() => {
                navigate(`/${PAGE_NAME_SELECT_MODEL}`);
              }}
            >
              <img src={start} alt="Start" className="size-[14vh]" />
              {/* トラブルシューティング開始 */}
            </button>
            <p className="font-bold text-xl">
              {t("トップ画面.トラブルシューティング開始")}
            </p>
          </div>
          <div className="flex gap-10 items-center py-12 justify-center">
            <div className="flex flex-col items-center justify-center gap-2">
              <button
                onClick={() => {
                  navigate(`/${PAGE_NAME_SEARCH_HISTORY}`);
                }}
              >
                <img
                  src={History}
                  alt="History"
                  className="size-[14vh] bg-white border-2 border-gray-300"
                />
                {/* 検索履歴 */}
              </button>
              {t("トップ画面.検索履歴")}
            </div>
            <UnansweredInfo unansweredNum={unansweredNum} />
          </div>
          <div className="flex flex-col items-center justify-center gap-2">
            <button
              onClick={() => {
                navigate(`/${PAGE_NAME_FAVORITE}`);
              }}
            >
              <img
                src={Favorite}
                alt="Favorite"
                className="size-[14vh] bg-white border-2 border-gray-300"
              />
              {/* お気に入り */}
            </button>
            {t("トップ画面.お気に入り")}
          </div>
        </div>
        <HamburgerMenu menuOpen={menuOpen} />
      </main>
    </div>
  );
};
